<script setup lang="ts">
import type { ClinicResponse, CityResponse } from '@/types'

interface SearchResult {
  clinics: ClinicResponse[]
  cities: Array<CityResponse & { clinicCount: number }>
}

const props = defineProps<{
  size?: 'sm' | 'md' | 'lg'
  placeholder?: string
  autofocus?: boolean
  radiusKm?: number
}>()

const emit = defineEmits<{
  search: [query: string]
}>()

const router = useRouter()
const { capture } = useAnalytics()
const query = ref('')
const isOpen = ref(false)
const highlightedIndex = ref(-1)
const containerRef = ref<HTMLDivElement | null>(null)

defineExpose({
  query,
  triggerSearch: () => handleSearch(),
})

const searchResults = ref<SearchResult>({ clinics: [], cities: [] })
const isLoading = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(query, (q) => {
  if (debounceTimer) clearTimeout(debounceTimer)

  if (!q || q.length < 2) {
    searchResults.value = { clinics: [], cities: [] }
    isLoading.value = false
    return
  }

  isLoading.value = true
  debounceTimer = setTimeout(async () => {
    try {
      const res = await $fetch<{ success: boolean; data: SearchResult }>('/api/search', {
        query: { q, limit: 6 },
      })
      searchResults.value = res.data
    } catch {
      searchResults.value = { clinics: [], cities: [] }
    } finally {
      isLoading.value = false
    }
  }, 200)
})
const hasResults = computed(() => {
  return (
    (searchResults.value?.clinics && searchResults.value.clinics.length > 0) ||
    (searchResults.value?.cities && searchResults.value.cities.length > 0)
  )
})
const showDropdown = computed(() => isOpen.value && query.value.length >= 2)

// Flatten results for keyboard navigation
const allResults = computed(() => {
  const results: Array<{ type: 'clinic' | 'city'; item: ClinicResponse | (CityResponse & { clinicCount: number }) }> = []
  if (searchResults.value?.cities) {
    searchResults.value.cities.forEach((city) => results.push({ type: 'city', item: city }))
  }
  if (searchResults.value?.clinics) {
    searchResults.value.clinics.forEach((clinic) => results.push({ type: 'clinic', item: clinic }))
  }
  return results
})

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  query.value = target.value
  highlightedIndex.value = -1
  isOpen.value = true
}

function onFocus() {
  isOpen.value = true
}

function onBlur() {
  setTimeout(() => {
    if (import.meta.client && !containerRef.value?.contains(document.activeElement)) {
      isOpen.value = false
    }
  }, 150)
}

function onKeydown(event: KeyboardEvent) {
  if (!showDropdown.value || !hasResults.value) {
    if (event.key === 'Enter') {
      handleSearch()
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, allResults.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1)
      break
    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0 && allResults.value[highlightedIndex.value]) {
        selectResult(allResults.value[highlightedIndex.value]!, highlightedIndex.value)
      } else {
        handleSearch()
      }
      break
    case 'Escape':
      isOpen.value = false
      highlightedIndex.value = -1
      break
  }
}

function selectResult(result: { type: 'clinic' | 'city'; item: ClinicResponse | (CityResponse & { clinicCount: number }) }, position: number) {
  capture('search_result_clicked', { query: query.value, result_type: result.type, position })
  isOpen.value = false
  if (result.type === 'clinic') {
    const clinic = result.item as ClinicResponse
    query.value = clinic.name
    router.push(`/${clinic.city.county.slug}/${clinic.city.slug}/${clinic.slug}`)
  } else {
    const city = result.item as CityResponse & { clinicCount: number }
    query.value = city.name
    if (props.radiusKm) {
      router.push({ path: '/veteriny', query: { citySlug: city.slug, radiusKm: props.radiusKm } })
    } else {
      router.push(`/${city.county.slug}/${city.slug}`)
    }
  }
}

function handleSearch() {
  capture('search_performed', { query: query.value, result_count: allResults.value.length })
  emit('search', query.value)

  // When radius is set and we have a matching city, navigate to city nearest mode
  if (props.radiusKm && searchResults.value?.cities?.length) {
    const city = searchResults.value.cities[0]!
    router.push({ path: '/veteriny', query: { citySlug: city.slug, radiusKm: props.radiusKm } })
  } else {
    const routeQuery = query.value.length >= 2 ? { q: query.value } : undefined
    router.push({ path: '/veteriny', query: routeQuery })
  }

  isOpen.value = false
}

function getResultIndex(type: 'city' | 'clinic', index: number): number {
  if (type === 'city') {
    return index
  }
  return (searchResults.value?.cities?.length || 0) + index
}

const inputSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'sm'
    case 'lg':
      return 'xl'
    default:
      return 'md'
  }
})
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <UInput
      :model-value="query"
      :placeholder="placeholder || 'Hledat veterináře...'"
      :size="inputSize"
      :autofocus="autofocus"
      icon="i-lucide-search"
      :loading="isLoading"
      class="w-full"
      autocomplete="off"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    >
      <template v-if="query.length > 0" #trailing>
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-x"
          size="xs"
          class="-mr-1"
          @click="query = ''; isOpen = false"
        />
      </template>
    </UInput>

    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="showDropdown"
        class="absolute z-50 w-full mt-1 bg-default rounded-lg shadow-lg border border-default overflow-hidden"
      >
        <div v-if="isLoading" class="p-4 text-center text-muted">
          <UIcon name="i-lucide-loader-2" class="w-5 h-5 animate-spin mx-auto" />
        </div>

        <div v-else-if="hasResults" class="max-h-96 overflow-y-auto">
          <!-- Cities Section -->
          <div v-if="searchResults?.cities?.length">
            <div class="px-3 py-2 text-xs font-medium text-muted uppercase tracking-wider bg-muted">
              Města
            </div>
            <button
              v-for="(city, index) in searchResults.cities"
              :key="`city-${city.id}`"
              type="button"
              class="w-full px-4 py-2.5 text-left hover:bg-muted transition-colors flex items-center justify-between gap-2"
              :class="{ 'bg-muted': getResultIndex('city', index) === highlightedIndex }"
              @mouseenter="highlightedIndex = getResultIndex('city', index)"
              @click="selectResult({ type: 'city', item: city }, getResultIndex('city', index))"
            >
              <div class="flex items-center gap-2 min-w-0">
                <UIcon name="i-lucide-map-pin" class="w-4 h-4 text-dimmed shrink-0" />
                <div class="min-w-0">
                  <div class="font-medium text-highlighted truncate">
                    {{ city.name }}
                  </div>
                  <div class="text-xs text-muted">
                    {{ city.county.name }}
                  </div>
                </div>
              </div>
              <UBadge color="neutral" variant="subtle" size="xs">
                {{ city.clinicCount }} klinik
              </UBadge>
            </button>
          </div>

          <!-- Clinics Section -->
          <div v-if="searchResults?.clinics?.length">
            <div class="px-3 py-2 text-xs font-medium text-muted uppercase tracking-wider bg-muted">
              Kliniky
            </div>
            <button
              v-for="(clinic, index) in searchResults.clinics"
              :key="`clinic-${clinic.id}`"
              type="button"
              class="w-full px-4 py-2.5 text-left hover:bg-muted transition-colors"
              :class="{ 'bg-muted': getResultIndex('clinic', index) === highlightedIndex }"
              @mouseenter="highlightedIndex = getResultIndex('clinic', index)"
              @click="selectResult({ type: 'clinic', item: clinic }, getResultIndex('clinic', index))"
            >
              <div>
                <div class="font-medium text-highlighted truncate">
                  {{ clinic.name }}
                </div>
                <div class="flex items-center gap-1.5 flex-wrap mt-1">
                  <div v-if="clinic.rating" class="flex items-center gap-1">
                    <RatingStars :rating="clinic.rating" size="sm" />
                    <span v-if="clinic.reviewCount" class="text-xs text-dimmed">({{ clinic.reviewCount }})</span>
                  </div>
                  <EmergencyBadge
                    v-if="clinic.isEmergency"
                    :is24h="clinic.is24Hours"
                    size="sm"
                  />
                  <PremiumBadge v-if="clinic.isVerified" variant="verified" size="sm" />
                  <PremiumBadge v-if="clinic.isPremium" variant="premium" size="sm" />
                </div>
                <div class="text-sm text-muted truncate mt-0.5">
                  {{ clinic.city.name }}, {{ clinic.city.county.name }}
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- No Results -->
        <div v-else class="p-6 text-center">
          <UIcon name="i-lucide-search-x" class="w-8 h-8 text-dimmed mx-auto mb-2" />
          <p class="text-muted">
            Žádné výsledky pro "{{ query }}"
          </p>
          <p class="text-sm text-dimmed mt-1">
            Zkuste jiné klíčové slovo
          </p>
        </div>

        <!-- View All Button -->
        <button
          v-if="hasResults"
          type="button"
          class="w-full px-4 py-3 text-sm font-medium text-primary hover:bg-muted transition-colors border-t border-default flex items-center justify-center gap-2"
          @click="handleSearch"
        >
          <UIcon name="i-lucide-search" class="w-4 h-4" />
          Zobrazit všechny výsledky pro "{{ query }}"
        </button>
      </div>
    </Transition>
  </div>
</template>
